// @flow

import {useState} from "react";
import {useStore} from "./useStore";
import {normalizeCityName} from "../utils/normalizeCityName";
import {reverseGeocodeAddress, forwardGeocodeAddress} from "../utils/nominatimGeocoding";

// İlçe/mahalle isim karşılaştırmasında yaygın ekleri temizler; il normalizasyonu
// (normalizeCityName) ile aynı kalıpta ama il'e özgü "asya/avrupa/tümü" temizliği yok.
//
// Regex \b Türkçe'ye özgü harflerde (ı, ö, ğ, ş, ç, İ) kelime sınırını doğru
// tanımıyor — ör. "Kadıköy" "Kadı" + "köy"e bölünüp "köy" eki yanlışlıkla
// kırpılabiliyordu. Bunun yerine boşlukla ayrılmış TOKEN'lar üzerinde tam
// eşleşme aranıyor (bkz. nominatimGeocoding.js'teki aynı yaklaşım).
const NAME_SUFFIX_WORDS = new Set([
    "mahallesi",
    "mahalle",
    "mah",
    "mh",
    "ilçesi",
    "ilçe",
    "belediyesi",
    "köyü",
    "köy"
]);

const normalizeName = value => {
    const lower = (value || "").toLocaleLowerCase("tr-TR").replace("merkez ilçe", " ");
    const withoutPunctuation = lower.replace(/[().]/g, " ");
    const tokens = withoutPunctuation.trim().split(/\s+/).filter(Boolean);
    return tokens
        .filter(token => !NAME_SUFFIX_WORDS.has(token))
        .join(" ")
        .trim();
};

const EMPTY_RESULT = {
    city: "",
    county: "",
    district: "",
    districtId: null,
    matchedLevel: "none",
    displayAddress: ""
};

// Alt firma oluşturma/düzenleme formlarında harita ↔ il/ilçe/mahalle senkronunu
// yöneten hook. Nominatim'in döndürdüğü ham isimleri, panelin kendi il/ilçe/mahalle
// referans listeleriyle (optionStore) eşleştirir; eşleşme bulunamayan seviyede
// alan boş bırakılır — geçersiz ham değer forma asla yazılmaz.
export const useSubCompanyLocationSync = () => {
    const stores = useStore();
    const [isResolving, setIsResolving] = useState(false);

    // Haritadan seçilen nokta -> DB ile doğrulanmış il/ilçe/mahalle.
    const resolveAddressFromLocation = async ({lat, lng}) => {
        setIsResolving(true);
        try {
            const geo = await reverseGeocodeAddress({lat, lng});
            if (!geo) return EMPTY_RESULT;

            const cities = await stores.optionStore.loadCities();
            const cityKey = normalizeCityName(geo.city);
            const matchedCity = cities.find(c => normalizeCityName(c.name) === cityKey);
            if (!matchedCity) {
                return {...EMPTY_RESULT, displayAddress: geo.displayAddress};
            }
            const city = matchedCity.name.toLocaleLowerCase("tr-TR");

            const districts = await stores.optionStore.loadDistricts({
                cityName: normalizeCityName(matchedCity.name)
            });
            const matchedDistrict = districts?.find(
                d => normalizeName(d.name) === normalizeName(geo.county)
            );
            if (!matchedDistrict) {
                return {
                    city,
                    county: "",
                    district: "",
                    districtId: null,
                    matchedLevel: "city",
                    displayAddress: geo.displayAddress
                };
            }
            const county = matchedDistrict.name.toLocaleLowerCase("tr-TR");

            const neighborhoodResponse = await stores.optionStore.getNeighborhoodList({
                districtId: matchedDistrict._id,
                limit: 1000,
                sort: "name"
            });
            const matchedNeighborhood = (neighborhoodResponse?.data || []).find(
                n => normalizeName(n.name) === normalizeName(geo.neighborhood)
            );

            return {
                city,
                county,
                district: matchedNeighborhood?.name || "",
                districtId: matchedDistrict._id,
                matchedLevel: matchedNeighborhood ? "district" : "county",
                displayAddress: geo.displayAddress
            };
        } finally {
            setIsResolving(false);
        }
    };

    // İl adı + ilçe adından (form değerleri) districtId çözer — mevcut formlardaki
    // tekrarlanan manuel eşleştirme bloğunun merkezi hali (cacheKey normalize edilmiş).
    const resolveDistrictId = async ({city, county}) => {
        if (!city || !county) return null;
        const districts = await stores.optionStore.loadDistricts({
            cityName: normalizeCityName(city)
        });
        return districts?.find(d => d.name?.toLocaleLowerCase("tr-TR") === county)?._id || null;
    };

    // İl/ilçe/mahalle (form değerleri) -> koordinat.
    const resolveLocationFromAddress = async ({city, county, district}) => {
        if (!city) return null;
        setIsResolving(true);
        try {
            return await forwardGeocodeAddress({
                city,
                county,
                neighborhood: district
            });
        } finally {
            setIsResolving(false);
        }
    };

    return {resolveAddressFromLocation, resolveDistrictId, resolveLocationFromAddress, isResolving};
};
